/**
 * portal-berechtigungen/api.ts — the Bereich 6 API layer, RECONCILED to the backend's
 * committed contract (portal-visibility.controller.ts — the deployed source of truth).
 *
 * Each function does the live/mock split: live -> `apiFetch` against the real routes;
 * mock -> the in-memory store in `mock.ts`. Keeping the split HERE means the hooks +
 * components are mode-agnostic.
 *
 * Routes (all under the controller prefix `admin/customer-portal`; apiFetch prepends
 * the API base, so paths start at `/admin/...`):
 *   GET    /admin/customer-portal/igb2b-kategorien
 *   GET    /admin/customer-portal/visibility-mode/:st/:sid
 *   PUT    /admin/customer-portal/visibility-mode/:st/:sid          { mode }
 *   GET    /admin/customer-portal/portal-gruppen/:st/:sid
 *   POST   /admin/customer-portal/portal-gruppen/:st/:sid           { name, branchencodes? }
 *   PATCH  /admin/customer-portal/portal-gruppen/:st/:sid/:groupId  { name }
 *   DELETE /admin/customer-portal/portal-gruppen/:st/:sid/:groupId
 *   POST   …/:groupId/kategorien                                    { branchencode }
 *   DELETE …/:groupId/kategorien/:branchencode
 *   POST   …/:groupId/mitglieder                                    { grantId? }  (null = primary)
 *   DELETE …/:groupId/mitglieder/:grantRef                          ('primary' | grant UUID)
 *
 * EVERY mutation returns the full `{ mode, groups }` snapshot; callers adopt it wholesale.
 * The member pool has NO dedicated endpoint — it is sourced from the existing portal grants
 * admin endpoint (GET /admin/customer-portal/grants/:st/:sid) plus the primary holder.
 */

import { apiClient, apiFetch } from '../../lib/api-client';
import { isLiveData } from '../../lib/data-mode';
import type { PortalGrantAdminItem } from '../administration/types';
import {
  MOCK_IGB2B_KATEGORIEN,
  mockAddKategorie,
  mockAddMember,
  mockCreateGruppe,
  mockDeleteGruppe,
  mockGetGruppen,
  mockGetMemberPool,
  mockGetMode,
  mockRemoveKategorie,
  mockRemoveMember,
  mockRenameGruppe,
  mockSetMode,
} from './mock';
import {
  grantIdOfRef,
  memberRefOf,
  type Igb2bKategorieDto,
  type PortalGroupsResponseDto,
  type PortalMemberOption,
  type PortalVisibilityMode,
  type PortalVisibilityModeDto,
} from './types';

const ADMIN = '/admin/customer-portal';
const subj = (subjectType: string, subjectId: string): string =>
  `${encodeURIComponent(subjectType)}/${encodeURIComponent(subjectId)}`;
const groupsBase = (subjectType: string, subjectId: string): string =>
  `${ADMIN}/portal-gruppen/${subj(subjectType, subjectId)}`;

// ── Reference data ──────────────────────────────────────────────────────────

/** GET /igb2b-kategorien — the fixed reference list for the category picker. */
export function fetchIgb2bKategorien(): Promise<Igb2bKategorieDto[]> {
  if (!isLiveData()) return Promise.resolve(MOCK_IGB2B_KATEGORIEN);
  return apiFetch<Igb2bKategorieDto[]>(`${ADMIN}/igb2b-kategorien`);
}

// ── Mode ────────────────────────────────────────────────────────────────────

/** GET the customer's portal-visibility mode + non-system group count. */
export function fetchPortalVisibilityMode(
  subjectType: string,
  subjectId: string,
): Promise<PortalVisibilityModeDto> {
  if (!isLiveData()) return Promise.resolve(mockGetMode(subjectType, subjectId));
  return apiFetch<PortalVisibilityModeDto>(`${ADMIN}/visibility-mode/${subj(subjectType, subjectId)}`);
}

/** PUT the mode; returns the full { mode, groups } snapshot (group-mode seeds the defaults). */
export function setPortalVisibilityMode(
  subjectType: string,
  subjectId: string,
  mode: PortalVisibilityMode,
): Promise<PortalGroupsResponseDto> {
  if (!isLiveData()) return Promise.resolve(mockSetMode(subjectType, subjectId, mode));
  return apiFetch<PortalGroupsResponseDto>(`${ADMIN}/visibility-mode/${subj(subjectType, subjectId)}`, {
    method: 'PUT',
    body: JSON.stringify({ mode }),
  });
}

// ── Groups (read) ────────────────────────────────────────────────────────────

/** GET the customer's { mode, groups } snapshot (templates + custom, then the system group). */
export function fetchPortalGruppen(
  subjectType: string,
  subjectId: string,
): Promise<PortalGroupsResponseDto> {
  if (!isLiveData()) return Promise.resolve(mockGetGruppen(subjectType, subjectId));
  return apiFetch<PortalGroupsResponseDto>(groupsBase(subjectType, subjectId));
}

// ── Member pool (sourced from the grants admin endpoint + the primary holder) ─

/** Map a grants list to the assignable pool: primary holder first, then non-revoked grants. */
function grantsToPool(grants: PortalGrantAdminItem[]): PortalMemberOption[] {
  const pool: PortalMemberOption[] = [
    { grantId: null, label: 'Hauptkontakt (Kunde)', email: '', status: 'primary' },
  ];
  for (const g of grants) {
    if (g.status === 'revoked' || g.revoked_at != null) continue;
    pool.push({
      grantId: g.id,
      label: g.display_name ?? g.email ?? g.role_label,
      email: g.email ?? '',
      status: g.status,
    });
  }
  return pool;
}

/** GET the assignable portal-user pool (primary holder + the customer's non-revoked grants). */
export function fetchPortalMemberPool(
  subjectType: string,
  subjectId: string,
): Promise<PortalMemberOption[]> {
  if (!isLiveData()) return Promise.resolve(mockGetMemberPool(subjectType, subjectId));
  return apiClient.admin.portalGrants(subjectType, subjectId).then(grantsToPool);
}

// ── Groups (granular mutations — each returns the full snapshot) ──────────────

/** POST a new group with its initial category set (members are added afterwards). */
export function createPortalGruppe(
  subjectType: string,
  subjectId: string,
  body: { name: string; branchencodes?: string[] },
): Promise<PortalGroupsResponseDto> {
  if (!isLiveData()) return Promise.resolve(mockCreateGruppe(subjectType, subjectId, body));
  return apiFetch<PortalGroupsResponseDto>(groupsBase(subjectType, subjectId), {
    method: 'POST',
    body: JSON.stringify({
      name: body.name,
      ...(body.branchencodes && body.branchencodes.length > 0
        ? { branchencodes: body.branchencodes }
        : {}),
    }),
  });
}

/** PATCH a group's name (the system group is not renamable). */
export function renamePortalGruppe(
  subjectType: string,
  subjectId: string,
  groupId: string,
  name: string,
): Promise<PortalGroupsResponseDto> {
  if (!isLiveData()) return Promise.resolve(mockRenameGruppe(subjectType, subjectId, groupId, name));
  return apiFetch<PortalGroupsResponseDto>(
    `${groupsBase(subjectType, subjectId)}/${encodeURIComponent(groupId)}`,
    { method: 'PATCH', body: JSON.stringify({ name }) },
  );
}

/** DELETE a group (never the system group — the UI hides its delete control). */
export function deletePortalGruppe(
  subjectType: string,
  subjectId: string,
  groupId: string,
): Promise<PortalGroupsResponseDto> {
  if (!isLiveData()) return Promise.resolve(mockDeleteGruppe(subjectType, subjectId, groupId));
  return apiFetch<PortalGroupsResponseDto>(
    `${groupsBase(subjectType, subjectId)}/${encodeURIComponent(groupId)}`,
    { method: 'DELETE' },
  );
}

/** POST one IGB2B category onto a (non-system) group. */
export function addPortalGruppeKategorie(
  subjectType: string,
  subjectId: string,
  groupId: string,
  branchencode: string,
): Promise<PortalGroupsResponseDto> {
  if (!isLiveData()) {
    return Promise.resolve(mockAddKategorie(subjectType, subjectId, groupId, branchencode));
  }
  return apiFetch<PortalGroupsResponseDto>(
    `${groupsBase(subjectType, subjectId)}/${encodeURIComponent(groupId)}/kategorien`,
    { method: 'POST', body: JSON.stringify({ branchencode }) },
  );
}

/** DELETE one IGB2B category from a (non-system) group. */
export function removePortalGruppeKategorie(
  subjectType: string,
  subjectId: string,
  groupId: string,
  branchencode: string,
): Promise<PortalGroupsResponseDto> {
  if (!isLiveData()) {
    return Promise.resolve(mockRemoveKategorie(subjectType, subjectId, groupId, branchencode));
  }
  return apiFetch<PortalGroupsResponseDto>(
    `${groupsBase(subjectType, subjectId)}/${encodeURIComponent(groupId)}/kategorien/${encodeURIComponent(branchencode)}`,
    { method: 'DELETE' },
  );
}

/** POST one member onto a (non-system) group (grantId null = the primary holder). */
export function addPortalGruppeMember(
  subjectType: string,
  subjectId: string,
  groupId: string,
  grantId: string | null,
): Promise<PortalGroupsResponseDto> {
  if (!isLiveData()) {
    return Promise.resolve(mockAddMember(subjectType, subjectId, groupId, grantId));
  }
  return apiFetch<PortalGroupsResponseDto>(
    `${groupsBase(subjectType, subjectId)}/${encodeURIComponent(groupId)}/mitglieder`,
    { method: 'POST', body: JSON.stringify({ grantId }) },
  );
}

/** DELETE one member from a (non-system) group (grantRef 'primary' | a grant UUID). */
export function removePortalGruppeMember(
  subjectType: string,
  subjectId: string,
  groupId: string,
  grantRef: string,
): Promise<PortalGroupsResponseDto> {
  if (!isLiveData()) {
    return Promise.resolve(
      mockRemoveMember(subjectType, subjectId, groupId, grantIdOfRef(grantRef)),
    );
  }
  return apiFetch<PortalGroupsResponseDto>(
    `${groupsBase(subjectType, subjectId)}/${encodeURIComponent(groupId)}/mitglieder/${encodeURIComponent(grantRef)}`,
    { method: 'DELETE' },
  );
}

// ── Editor save orchestration (diff desired-vs-current → granular calls) ──────

/** The editor's desired end-state for one group (name + category set + member set). */
export interface PortalGruppeDraft {
  name: string;
  branchencodes: string[];
  /** Member selection as grant-refs ('primary' | grant UUID). */
  memberRefs: string[];
}

/** The current persisted state of the group being edited (for the diff). */
export interface PortalGruppeCurrent {
  name: string;
  branchencodes: string[];
  memberRefs: string[];
}

const diff = (desired: string[], current: string[]) => {
  const cur = new Set(current);
  const des = new Set(desired);
  return {
    added: desired.filter((x) => !cur.has(x)),
    removed: current.filter((x) => !des.has(x)),
  };
};

/**
 * Persist an editor draft with GRANULAR calls, adopting the snapshot from the last call.
 * CREATE (groupId null): POST the group with its initial categories in one call, then add
 * each selected member. EDIT: rename if changed, then diff categories + members and issue
 * the add/remove granular calls. The final `{ mode, groups }` snapshot is returned.
 */
export async function savePortalGruppe(
  subjectType: string,
  subjectId: string,
  input: {
    groupId: string | null;
    draft: PortalGruppeDraft;
    current?: PortalGruppeCurrent;
  },
): Promise<PortalGroupsResponseDto> {
  const { groupId, draft } = input;

  // ── CREATE ──
  if (groupId === null) {
    let snapshot = await createPortalGruppe(subjectType, subjectId, {
      name: draft.name,
      branchencodes: draft.branchencodes,
    });
    const created = snapshot.groups.find(
      (g) => !g.istSystem && g.name.toLowerCase() === draft.name.trim().toLowerCase(),
    );
    if (!created) return snapshot;
    for (const ref of draft.memberRefs) {
      snapshot = await addPortalGruppeMember(subjectType, subjectId, created.id, grantIdOfRef(ref));
    }
    return snapshot;
  }

  // ── EDIT ──
  const current: PortalGruppeCurrent = input.current ?? {
    name: draft.name,
    branchencodes: [],
    memberRefs: [],
  };
  let snapshot: PortalGroupsResponseDto | null = null;

  if (draft.name.trim() !== current.name.trim()) {
    snapshot = await renamePortalGruppe(subjectType, subjectId, groupId, draft.name);
  }

  const cats = diff(draft.branchencodes, current.branchencodes);
  for (const code of cats.added) {
    snapshot = await addPortalGruppeKategorie(subjectType, subjectId, groupId, code);
  }
  for (const code of cats.removed) {
    snapshot = await removePortalGruppeKategorie(subjectType, subjectId, groupId, code);
  }

  const members = diff(draft.memberRefs, current.memberRefs);
  for (const ref of members.added) {
    snapshot = await addPortalGruppeMember(subjectType, subjectId, groupId, grantIdOfRef(ref));
  }
  for (const ref of members.removed) {
    snapshot = await removePortalGruppeMember(subjectType, subjectId, groupId, ref);
  }

  // No-op edit — re-read so callers always adopt a valid snapshot.
  return snapshot ?? fetchPortalGruppen(subjectType, subjectId);
}

/** Build the current-state diff basis from a persisted group's members. */
export { memberRefOf };
